import React from 'react';
import styled from 'styled-components';
import Modal from 'components/modal';
import Button from 'components/button';
import Icon from 'components/icon';
import imageLoader from 'utils/imageLoader';
import useMobile from 'hooks/useMobile';
import MainImage from './main-image';
import Filmstrip from './filmstrip';
import useZoom from './use-zoom';

// Preload window around the current image: the visible image jumps the queue,
// its neighbors follow, everything else (thumbnails) loads at low priority.
const PRELOAD_WINDOW = [
  { offset: 0, priority: 100 },
  { offset: 1, priority: 50 },
  { offset: -1, priority: 50 },
  { offset: 2, priority: 40 },
  { offset: -2, priority: 40 }
];

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  user-select: none;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  color: #fff;
  z-index: 2;
`;

const Counter = styled.div`
  font-size: 14px;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.85);
`;

const CloseButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  font-size: 14px;
  letter-spacing: 1px;
  padding: 8px 14px;
  border-radius: 20px;
  transition: background-color 0.2s ease;
  &:hover {
    background-color: rgba(255, 255, 255, 0.12);
  }
  i {
    font-size: 18px;
  }
`;

const StageArea = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  padding: 0px 72px;
  @media (max-width: 835px) {
    padding: 0px;
  }
`;

const NavButton = styled(Button)`
  position: absolute;
  top: calc(50% - 24px);
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #fff;
  font-size: 18px;
  background-color: rgba(255, 255, 255, 0.12);
  transition: background-color 0.2s ease;
  z-index: 2;
  &:hover {
    background-color: rgba(255, 255, 255, 0.25);
  }
  &.left {
    left: 12px;
  }
  &.right {
    right: 12px;
  }
  @media (max-width: 835px) {
    width: 40px;
    height: 40px;
    top: calc(50% - 20px);
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 100%;
  color: rgba(255, 255, 255, 0.6);
  i {
    font-size: 40px;
  }
`;

const clampIndex = (value, length) => Math.min(Math.max(value, 0), Math.max(length - 1, 0));

const Viewer = ({ urls = [], startIndex = 0, onClose }) => {
  const [index, setIndex] = React.useState(() => clampIndex(startIndex, urls.length));
  const { isTabletOrMobile } = useMobile();

  const goPrev = React.useCallback(
    () => setIndex((prev) => clampIndex(prev - 1, urls.length)),
    [urls.length]
  );
  const goNext = React.useCallback(
    () => setIndex((prev) => clampIndex(prev + 1, urls.length)),
    [urls.length]
  );

  const zoom = useZoom({ onSwipeLeft: goNext, onSwipeRight: goPrev });
  const { isZoomed, reset: resetZoom } = zoom;

  React.useEffect(() => {
    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        // First Escape zooms back out, second closes
        if (isZoomed) {
          resetZoom();
        } else {
          onClose && onClose();
        }
        return;
      }
      if (!urls.length) return;
      if (event.key === 'ArrowLeft') goPrev();
      if (event.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [urls.length, goPrev, goNext, onClose, isZoomed, resetZoom]);

  // Navigating to another image always starts it at fit-to-screen
  React.useEffect(() => {
    resetZoom();
  }, [index, resetZoom]);

  // Lock body scroll while the viewer is open
  React.useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Preload the current image and its neighbors; drop anything still queued on close
  React.useEffect(() => {
    PRELOAD_WINDOW.forEach(({ offset, priority }) => {
      const url = urls[index + offset];
      if (url) imageLoader.load(url, { priority }).catch(() => {});
    });
  }, [index, urls]);

  React.useEffect(() => () => imageLoader.cancelPending(), []);

  // Guard against the urls list shrinking while open
  React.useEffect(() => {
    setIndex((prev) => clampIndex(prev, urls.length));
  }, [urls.length]);

  if (!urls.length) {
    return (
      <Wrapper>
        <TopBar>
          <div />
          <CloseButton onClick={onClose}>
            <Icon name="fa-light fa-xmark" />
            {!isTabletOrMobile && 'CLOSE'}
          </CloseButton>
        </TopBar>
        <EmptyState>
          <Icon name="fa-light fa-images" />
          <div>No images to display</div>
        </EmptyState>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <TopBar>
        <Counter>
          {index + 1} / {urls.length}
        </Counter>
        <CloseButton onClick={onClose}>
          <Icon name="fa-light fa-xmark" />
          {!isTabletOrMobile && 'CLOSE'}
        </CloseButton>
      </TopBar>
      <StageArea>
        <MainImage url={urls[index]} alt={`Image ${index + 1} of ${urls.length}`} zoom={zoom} />
        {index > 0 && (
          <NavButton className="left" onClick={goPrev} aria-label="Previous image">
            <Icon name="fa-solid fa-chevron-left" />
          </NavButton>
        )}
        {index + 1 < urls.length && (
          <NavButton className="right" onClick={goNext} aria-label="Next image">
            <Icon name="fa-solid fa-chevron-right" />
          </NavButton>
        )}
      </StageArea>
      {urls.length > 1 && <Filmstrip urls={urls} activeIndex={index} onSelect={setIndex} />}
    </Wrapper>
  );
};

export default ({ open, urls = [], startIndex = 0, onClose }) => {
  return (
    <Modal width="100vw" height="100vh" maxHeight="100vh" radius={0} color="#111" open={open}>
      <Viewer urls={urls} startIndex={startIndex} onClose={onClose} />
    </Modal>
  );
};
